import { Alert, Box, Button, Collapse, Drawer, Modal, Skeleton, Stack, Table, Text } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { ArrowsClockwiseIcon, WarningIcon } from "@phosphor-icons/react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import useSwr from "swr";
import { ARApi } from "../../apis";
import { IGapPair, IGapRunResult, IVerifyPair, IVerifyReport } from "../../apis/ar";
import { Page, PageHeader } from "../../components/layout/Page";
import { usePermission } from "../../components/permission";
import { ModalFooter } from "../../components/ui/app-modal";
import modalChrome from "../../components/ui/app-modal.module.css";
import { currencyFormat } from "../../utils";
import { dateFormat } from "../../utils/date";
import { Notice } from "../../components/ui/notice";
import { message } from "../../utils/Message";
import classes from "./ar-ledger.module.css";
import { ListStates, resolveListStates } from "../../components/list";

const SWR_KEY = "ar.verify.report";
const COLUMN_COUNT = 6;
const SKELETON_ROWS = 4;

const money = (v: number) => currencyFormat(Number(v || 0)).replace(/\s*₮$/, "");
const signed = (v: number) => (v < 0 ? `−${money(Math.abs(v))}` : v > 0 ? `+${money(v)}` : money(0));
const pairKey = (p: { sellerBusinessId: string; buyerBusinessId: string }) => `${p.sellerBusinessId}::${p.buyerBusinessId}`;

/** Drawer target: one pair, or every pair the signed-in business can write (banner button). */
type Target = IVerifyPair | "ALL";

const pillClass = (type: string) =>
  type === "INVOICE"
    ? classes.pillInvoice
    : type === "PAYMENT"
      ? classes.pillPayment
      : type === "CREDIT_NOTE" || type === "DEBIT_NOTE"
        ? classes.pillAmend
        : classes.pillOther;

/**
 * AR ledger reconciliation (canvas: ARLedger). Compares the AR sub-ledger with
 * the balance computed straight from invoices (and the general ledger 1200.*
 * where the signed-in business is the seller). A mismatching pair is repaired
 * with the gap backfill: preview first (dry-run, writes nothing), then confirm.
 * The old "rebuild" action is gone — it deleted a pair's rows and could not
 * restore cancellations, amendments or earlier gap rows.
 */
export const ARLedgerPage = () => {
  const { t } = useTranslation(["billing", "common"]);
  const dash = t("common:list.dash");
  // Writing to the AR ledger needs INV_SETT_SEND (the API enforces it); LIST only opens the preview.
  const canWrite = usePermission("INV_SETT_SEND").hasAccess;

  const [expanded, setExpanded] = useState<string | null>(null);
  const [target, setTarget] = useState<Target | null>(null);
  const [busy, setBusy] = useState(false);

  const { data, error, isLoading, isValidating, mutate } = useSwr<IVerifyReport>(
    SWR_KEY,
    async () => await ARApi.getVerifyReport(),
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );

  const pairs = data?.pairs ?? [];
  const checked = data?.checked ?? 0;
  const mismatched = data?.mismatched ?? 0;
  const myId = data?.businessId ?? null;
  const showError = !!error && !isValidating;
  const showSkeleton = isLoading || (isValidating && !data && !error);
  const ready = !!data && !showError;
  const canFill = (p: IVerifyPair) => p.mismatched && !!myId && p.sellerBusinessId === myId;
  const writable = pairs.filter(canFill);

  const recheck = async () => {
    setBusy(true);
    try {
      await mutate();
      message.success(t("billing:arLedger.recheckDone"));
    } catch (err) {
      message.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const exportCsv = () => {
    const header = ["seller", "buyer", "invoice", "ar", "gl1200", "diff"];
    const lines = pairs.map((p) =>
      [p.sellerName, p.buyerName, p.reference, p.ledger, p.gl ?? "", p.diff]
        .map((v) => `"${String(v).replace(/"/g, "\"\"")}"`)
        .join(","),
    );
    const blob = new Blob([[header.join(","), ...lines].join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ar-ledger-reconciliation-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const sorted = [...pairs].sort((a, b) => Number(b.mismatched) - Number(a.mismatched));

  const renderRow = (p: IVerifyPair) => {
    const key = pairKey(p);
    const isOpen = expanded === key;
    return (
      <React.Fragment key={key}>
        <Table.Tr
          className={classes.row}
          tabIndex={0}
          aria-expanded={isOpen}
          onClick={() => setExpanded(isOpen ? null : key)}
          onKeyDown={(ev) => {
            if (ev.target === ev.currentTarget && (ev.key === "Enter" || ev.key === " ")) {
              ev.preventDefault();
              setExpanded(isOpen ? null : key);
            }
          }}
        >
          <Table.Td className={classes.stickyActions} onClick={(e) => e.stopPropagation()}>
            {canFill(p) ? (
              <Button variant="default" size="compact-sm" disabled={busy} onClick={() => setTarget(p)}>
                {t(canWrite ? "billing:arLedger.fill" : "billing:arLedger.preview")}
              </Button>
            ) : (
              <Text span className={classes.muted}>{dash}</Text>
            )}
          </Table.Td>
          <Table.Td>
            <Box className={classes.pairName}>
              <Text span className={classes.seller}>{p.sellerName}</Text>
              <Text span className={classes.buyer}>→ {p.buyerName}</Text>
            </Box>
          </Table.Td>
          <Table.Td className={classes.num}>{money(p.reference)}</Table.Td>
          <Table.Td className={classes.num}>{money(p.ledger)}</Table.Td>
          <Table.Td className={classes.num}>
            {p.gl === null ? <Text span className={classes.muted}>{dash}</Text> : money(p.gl)}
          </Table.Td>
          <Table.Td className={classes.num}>
            {p.diff === 0 ? (
              <Text span className={classes.diffZero}>{dash}</Text>
            ) : (
              <Text span className={classes.diffBad}>{signed(p.diff)}</Text>
            )}
          </Table.Td>
        </Table.Tr>

        <Table.Tr className={classes.collapseRow}>
          <Table.Td colSpan={COLUMN_COUNT} p={0}>
            <Collapse expanded={isOpen}>
              <Box className={classes.detail}>
                {p.comparisons.length === 0 ? (
                  <Text size="sm" c="dimmed">{t("billing:arLedger.detailMatched")}</Text>
                ) : (
                  <>
                    <Box className={`${classes.detailRow} ${classes.detailHead}`}>
                      <span />
                      <span className={classes.num}>{t("billing:arLedger.colInvoice")}</span>
                      <span className={classes.num}>{t("billing:arLedger.colAr")}</span>
                      <span className={classes.num}>{t("billing:arLedger.colDiff")}</span>
                    </Box>
                    {p.comparisons.map((c) => (
                      <Box key={c.field} className={classes.detailRow}>
                        <Text span c="dimmed">{t(`billing:arLedger.field.${c.field}`)}</Text>
                        <span className={classes.num}>{money(c.reference)}</span>
                        <span className={classes.num}>{money(c.ledger)}</span>
                        <span className={`${classes.num} ${classes.diffBad}`}>{signed(c.diff)}</span>
                      </Box>
                    ))}
                  </>
                )}
              </Box>
            </Collapse>
          </Table.Td>
        </Table.Tr>
      </React.Fragment>
    );
  };

  const listStates = resolveListStates(
    <ListStates
      loading={showSkeleton}
      error={showError ? error : undefined}
      onRetry={() => mutate()}
      empty={showError || showSkeleton || sorted.length === 0}
      columns={COLUMN_COUNT}
      emptyTitle={t("billing:arLedger.empty")}
    />
  );

  const renderBody = () => {
    return sorted.map(renderRow);
  };

  return (
    <Page>
      <PageHeader
        title={
          <>
            {t("billing:arLedger.title")}
            {ready && checked > 0 && <Text span className={classes.titleChip}>{t("billing:arLedger.pairsChip", { count: checked })}</Text>}
          </>
        }
        subtitle={t("billing:arLedger.subtitle", {
          at: data?.checkedAt ? new Date(data.checkedAt).toLocaleString("sv-SE").slice(0, 16) : dash,
        })}
        extra={
          <>
            <Button variant="default" onClick={exportCsv} disabled={!ready || pairs.length === 0}>
              {t("billing:arLedger.exportCsv")}
            </Button>
            <Button variant="default" loading={busy || (isValidating && !isLoading)} onClick={recheck} leftSection={<ArrowsClockwiseIcon size={14} />}>
              {t("billing:arLedger.recheck")}
            </Button>
          </>
        }
      />

      {ready && checked > 0 && mismatched === 0 && (
        <Box className={`${classes.status} ${classes.statusOk}`} role="status">
          <Box className={classes.statusText}>
            <Text span className={classes.statusTitle}>{t("billing:arLedger.allMatched")}</Text>
          </Box>
        </Box>
      )}
      {ready && checked > 0 && mismatched > 0 && (
        <Notice
          tone="danger"
          title={t("billing:arLedger.someMismatched", { total: checked, count: mismatched })}
          action={
            writable.length > 0 && canWrite ? (
              <Button variant="default" color="red" disabled={busy} onClick={() => setTarget("ALL")}>
                {t("billing:arLedger.fillAll")}
              </Button>
            ) : undefined
          }
        >
          {t("billing:arLedger.mismatchBody")}
        </Notice>
      )}

      <Box className={classes.card}>
        <Box className={classes.wrap}>
          {listStates ?? (
          <Table className={classes.table} withRowBorders={false} horizontalSpacing={0} verticalSpacing={0}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th className={classes.stickyActionsHead}>{t("billing:arLedger.colActions")}</Table.Th>
                <Table.Th>{t("billing:arLedger.colPair")}</Table.Th>
                <Table.Th ta="right">{t("billing:arLedger.colInvoice")}</Table.Th>
                <Table.Th ta="right">{t("billing:arLedger.colAr")}</Table.Th>
                <Table.Th ta="right">{t("billing:arLedger.colGl")}</Table.Th>
                <Table.Th ta="right">{t("billing:arLedger.colDiff")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{renderBody()}</Table.Tbody>
          </Table>
          )}
        </Box>
      </Box>

      <GapDrawer
        target={target}
        pairs={pairs}
        canWrite={canWrite}
        onClose={() => setTarget(null)}
        onApplied={async () => {
          setTarget(null);
          await mutate();
        }}
      />
    </Page>
  );
};

type DrawerProps = {
  target: Target | null;
  pairs: IVerifyPair[];
  canWrite: boolean;
  onClose: () => void;
  onApplied: () => Promise<void>;
};

/** Gap backfill preview (dry-run) with a confirm step before anything is written. */
function GapDrawer({ target, pairs, canWrite, onClose, onApplied }: DrawerProps) {
  const { t } = useTranslation(["billing", "common"]);
  const isNarrow = useMediaQuery("(max-width: 640px)");
  const [confirming, setConfirming] = useState(false);
  const [applying, setApplying] = useState(false);

  const buyerBusinessId = target && target !== "ALL" ? target.buyerBusinessId : undefined;
  const swrKey = target ? ["ar.gap.preview", buyerBusinessId ?? "ALL"] : null;
  const { data, error, isLoading, mutate } = useSwr<IGapRunResult>(
    swrKey,
    async () => await ARApi.gapBackfill({ buyerBusinessId, dryRun: true }),
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );

  const nameOf = (p: IGapPair) => {
    const known = pairs.find((x) => pairKey(x) === pairKey(p));
    return known ? { seller: known.sellerName, buyer: known.buyerName } : { seller: p.sellerBusinessId, buyer: p.buyerBusinessId };
  };
  const previewPairs = data?.pairs ?? [];
  const rowCount = previewPairs.reduce((s, p) => s + p.rows.length, 0);
  const unrecCount = previewPairs.reduce((s, p) => s + p.unreconciled.length, 0);

  const apply = async () => {
    setApplying(true);
    try {
      const result = await ARApi.gapBackfill({ buyerBusinessId, dryRun: false });
      const failures = result.written?.failures ?? [];
      if (failures.length > 0) message.error(failures.join("; "));
      else message.success(t("billing:arLedger.applyDone", { rows: result.written?.posted ?? 0 }));
      setConfirming(false);
      await onApplied();
    } catch (err) {
      message.error((err as Error).message);
    } finally {
      setApplying(false);
    }
  };

  const typeLabel = (x: string) => t(`billing:arLedger.txType.${x}`, { defaultValue: x });
  const title = target === "ALL" ? t("billing:arLedger.drawerAll") : target ? `${target.sellerName} → ${target.buyerName}` : "";

  return (
    <>
      <Drawer
        opened={target !== null}
        onClose={onClose}
        position="right"
        size={isNarrow ? "100%" : 600}
        withCloseButton={false}
        classNames={{ body: classes.drawerBody, content: classes.drawerContent, header: classes.drawerChromeHeader }}
        title={null}
      >
        <Box className={classes.drawerInner}>
          <Box className={classes.drawerScroll}>
            <Box className={classes.drawerHeader}>
              <Text className={classes.drawerTitle}>{title}</Text>
              {target && target !== "ALL" && data && previewPairs[0] && (
                <Text className={classes.drawerFlow}>
                  {t("billing:arLedger.nowThen", { before: money(previewPairs[0].before), after: money(previewPairs[0].after) })}
                </Text>
              )}
              <Text className={classes.drawerFlow}>{t("billing:arLedger.dryRunNote")}</Text>
            </Box>

            {isLoading && (
              <Stack gap="sm" mt="lg">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} h={18} />)}</Stack>
            )}
            {error && !data && (
              <Stack gap="sm" mt="lg">
                <Alert color="red" radius="md" icon={<WarningIcon size={16} weight="bold" />}>
                  {(error as Error).message || t("billing:arLedger.loadError")}
                </Alert>
                <Button variant="default" onClick={() => mutate()}>{t("billing:arLedger.recheck")}</Button>
              </Stack>
            )}
            {data && previewPairs.length === 0 && (
              <Text mt="lg" c="dimmed">{t("billing:arLedger.nothingToWrite")}</Text>
            )}

            {data && previewPairs.map((p) => {
              const names = nameOf(p);
              return (
                <Box key={pairKey(p)} className={classes.section}>
                  {target === "ALL" && (
                    <>
                      <Text className={classes.drawerTitle}>{names.seller} → {names.buyer}</Text>
                      <Text className={classes.drawerFlow}>
                        {t("billing:arLedger.nowThen", { before: money(p.before), after: money(p.after) })}
                      </Text>
                    </>
                  )}
                  {p.rows.length > 0 && (
                    <Box className={classes.previewCard} mt="sm">
                      <Table className={classes.previewTable} data-density="compact" withRowBorders={false} horizontalSpacing={0} verticalSpacing={0}>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>{t("billing:arLedger.colDate")}</Table.Th>
                            <Table.Th>{t("billing:arLedger.colType")}</Table.Th>
                            <Table.Th>{t("billing:arLedger.colInvoiceRef")}</Table.Th>
                            <Table.Th ta="right">{t("billing:arLedger.colAmount")}</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {p.rows.map((r, i) => (
                            <Table.Tr key={`${r.invoiceId}-${r.kind}-${i}`}>
                              <Table.Td>{dateFormat(r.date)}</Table.Td>
                              <Table.Td><Text span className={`${classes.pill} ${pillClass(r.transactionType)}`}>{typeLabel(r.transactionType)}</Text></Table.Td>
                              <Table.Td>{r.refCode}</Table.Td>
                              <Table.Td className={`${classes.num} ${r.delta < 0 ? classes.minus : classes.plus}`}>{signed(r.delta)}</Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    </Box>
                  )}
                  {p.unreconciled.length > 0 && (
                    <Box mt="md">
                      <Text className={classes.sectionTitle}>{t("billing:arLedger.unrecTitle", { count: p.unreconciled.length })}</Text>
                      {p.unreconciled.map((u) => (
                        <Box key={u.invoiceId} className={classes.unrec}>
                          <b>{u.refCode}</b>{" · "}
                          {u.reason === "OVERPAID" && u.overpaid
                            ? t("billing:arLedger.unrecOverpaid", {
                                confirmed: money(u.overpaid.confirmed),
                                paid: money(u.overpaid.paid),
                              })
                            : t("billing:arLedger.unrecMismatch", { after: money(u.netAfter), expected: money(u.expected) })}
                          <Text span className={classes.unrecHint}>{t("billing:arLedger.unrecHint")}</Text>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>

          <Box className={classes.drawerFooter}>
            <Button variant="default" onClick={onClose}>{t("common:cancel")}</Button>
            {canWrite && (
              <Button color="brand" disabled={!data || rowCount === 0} onClick={() => setConfirming(true)}>
                {t("billing:arLedger.writeRows", { count: rowCount })}
              </Button>
            )}
          </Box>
        </Box>
      </Drawer>

      <Modal
        opened={confirming}
        onClose={() => setConfirming(false)}
        title={t("billing:arLedger.confirmTitle")}
        size="sm"
        classNames={{ body: modalChrome.modalBodyFlush }}
      >
        <Box px={24} pb={20}>
          <Alert color="red" radius="md" icon={<WarningIcon size={18} weight="bold" />}>
            {t("billing:arLedger.confirmWarning")}
          </Alert>
          <Text fz="sm" c="dimmed" mt="md">
            {t("billing:arLedger.confirmSummary", { rows: rowCount, unreconciled: unrecCount })}
          </Text>
        </Box>
        <ModalFooter>
          <Button variant="default" onClick={() => setConfirming(false)} disabled={applying}>{t("common:cancel")}</Button>
          <Button color="brand" loading={applying} onClick={apply}>{t("billing:arLedger.writeRows", { count: rowCount })}</Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default ARLedgerPage;
